import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import IconButton from "components/icon-button";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import useSaveTemplatesMutation from "mutations/useSaveTemplatesMutation";
import StyledInput from "notes-and-forms-v2/components/styled-input/StyledInput";
import TemplateContent from "notes-and-forms-v2/components/template-content/TemplateContent";
import getTemplateFromSchema from "notes-and-forms-v2/util/getTemplateFromSchema";
import { useState } from "react";
import { Button, Col, Row, Tab, Tabs } from "react-bootstrap";
import getClasses from "utils/getClasses";
import styles from "./PrebuiltTemplatesModal.module.scss";
import {
  PREBUILT_FORMS_EXAMPLE,
  PREBUILT_NOTES_EXAMPLE,
} from "./prebuiltTemplatesExample";

const PrebuiltTemplatesModal = ({
  templateType,
  show,
  onHide,
  onTemplatesImported,
}: {
  templateType: TemplateType;
  show: boolean;
  onHide: () => void;
  onTemplatesImported: (templates: NoteOrFormTemplate[]) => void;
}) => {
  const [activeTab, setActiveTab] = useState(
    templateType === "note" ? "notes" : "forms",
  );

  const [selectedTemplates, setSelectedTemplates] = useState<
    Pick<NoteOrFormTemplate, "title" | "type" | "elements">[]
  >([]);
  const [previewedTemplate, setPreviewedTemplate] =
    useState<Pick<NoteOrFormTemplate, "title" | "type" | "elements">>();

  const templates =
    activeTab === "notes" ? PREBUILT_NOTES_EXAMPLE : PREBUILT_FORMS_EXAMPLE;

  const saveTemplatesMutation = useSaveTemplatesMutation();

  return (
    <ModalLayout
      centered={true}
      size={"lg"}
      showHeaderDivider={true}
      show={show}
      error={saveTemplatesMutation.error ?? undefined}
      Header={
        <>
          Add Pre-built Templates to your Library
          <IconButton
            className={styles["prebuilt-templates__close"]}
            onClick={() => {
              onHide();
            }}
          >
            <OutlineCross />
          </IconButton>
        </>
      }
      Footer={
        <>
          <Button
            variant={"outline-primary"}
            onClick={() => {
              onHide();
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={
              saveTemplatesMutation.isPending || !selectedTemplates.length
            }
            onClick={() => {
              saveTemplatesMutation.mutate(selectedTemplates, {
                onSuccess: (data) => {
                  onHide();
                  onTemplatesImported(data.map(getTemplateFromSchema));
                },
              });
            }}
          >
            Add to Template Library
          </Button>
        </>
      }
      headerClass={styles["header"]}
      bodyClass={styles["body"]}
      footerClass={styles["footer"]}
      dialogClassName={styles["dialog"]}
      contentClassName={styles["content"]}
      className={styles["prebuilt-templates"]}
    >
      <Row className={styles["prebuilt-templates__row"]}>
        <Col className={styles["prebuilt-templates__left"]}>
          <div className={styles["prebuilt-templates__left-top"]}>
            <div className={styles["prebuilt-templates__left-text"]}>
              Simplify documentation with pre-built templates. All selected
              templates will be added to your library, under corresponding
              category.
            </div>

            <Tabs
              activeKey={activeTab}
              id="prebuilt-templates-tabs"
              onSelect={(k) => setActiveTab(k || "notes")}
              className={styles["prebuilt-templates__tabs"]}
            >
              <Tab
                eventKey="notes"
                title="Clinical Notes"
                tabClassName={styles["prebuilt-templates__tab"]}
              />
              <Tab
                eventKey="forms"
                title="Forms"
                tabClassName={styles["prebuilt-templates__tab"]}
              />
              {/* <Tab
                eventKey="assessments"
                title="Assessments"
                tabClassName={styles["prebuilt-templates__tab"]}
                disabled={templateType === "note" || templateType === "form"}
              /> */}
            </Tabs>
          </div>
          <div className={styles["prebuilt-templates__left-bottom"]}>
            {activeTab === "notes" && (
              <>
                <div
                  className={styles["prebuilt-templates__left-bottom-title"]}
                >
                  Progress Notes
                </div>

                <div className={styles["prebuilt-templates__left-bottom-text"]}>
                  Templates to record the observations, interventions, and
                  outcomes from each appointment
                </div>
              </>
            )}

            {activeTab === "forms" && (
              <>
                <div
                  className={styles["prebuilt-templates__left-bottom-title"]}
                >
                  Forms
                </div>
                <div className={styles["prebuilt-templates__left-bottom-text"]}>
                  Templates to record the observations, interventions, and
                  outcomes from each appointment
                </div>
              </>
            )}

            {templates.map((template) => (
              <div
                key={template.title}
                className={getClasses(
                  styles.item,
                  template.title === previewedTemplate?.title &&
                    styles.selected,
                )}
                onClick={(event) => {
                  if (
                    (event.target as HTMLElement).tagName === "INPUT" &&
                    !(event.target as HTMLInputElement).checked
                  ) {
                    return;
                  }
                  setPreviewedTemplate(template);
                }}
              >
                <StyledInput
                  type="checkbox"
                  value={template.title}
                  checked={selectedTemplates.includes(template)}
                  onChange={(e) =>
                    setSelectedTemplates((prev) =>
                      e.target.checked
                        ? [...prev, template]
                        : prev.filter((t) => t.title !== template.title),
                    )
                  }
                />
                {template.title}
              </div>
            ))}
          </div>
        </Col>
        <Col className={styles["prebuilt-templates__right"]}>
          {!previewedTemplate ? (
            <div className={styles["prebuilt-templates__right-empty"]}>
              <div className={styles["prebuilt-templates__right-empty-title"]}>
                No Template Preview
              </div>
              <div className={styles["prebuilt-templates__right-empty-text"]}>
                Select a template from the list to preview the content.
              </div>
            </div>
          ) : (
            <>
              <div className={styles["prebuilt-templates__right-title"]}>
                <h2>{previewedTemplate?.title}</h2>
              </div>
              <div className={styles["prebuilt-templates__right-text"]}>
                <TemplateContent
                  templateElements={previewedTemplate?.elements ?? []}
                  templateData={{}}
                />
              </div>
            </>
          )}
        </Col>
      </Row>
    </ModalLayout>
  );
};

export default PrebuiltTemplatesModal;
